import React, { Component } from "react";
import {
  Table,
  Message,
  Label,
  Segment,
  Header,
  Icon,
} from "semantic-ui-react";
import { fineApi } from "@api/fine/fine";
import { DateTime } from "luxon";
import PropTypes from "prop-types";

class OutstandingFinesTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      outstandingFines: [],
      loading: false,
      error: null,
    };
  }

  componentDidMount() {
    this.fetchOutstandingFines();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.fromDate !== this.props.fromDate ||
      prevProps.toDate !== this.props.toDate
    ) {
      this.fetchOutstandingFines();
    }
  }

  fetchOutstandingFines = async () => {
    const { fromDate, toDate } = this.props;
    this.setState({ loading: true, error: null });

    try {
      const activeFinesResponse = await fineApi.getActiveFines();

      const activeFines = activeFinesResponse.data.hits.filter(
        (fine) =>
          fine.status !== "resolved" &&
          fine.status !== "cancelled" &&
          fine.status !== "waived"
      );

      const outstandingFines = this.processOutstandingFines(activeFines);

      // Filter by date range if specified
      let filteredFines = outstandingFines;
      if (fromDate || toDate) {
        filteredFines = outstandingFines.filter((patron) => {
          return patron.fineBreakdown.some((fine) => {
            if (!fine.date) return true;
            const fineDate = DateTime.fromISO(fine.date);
            if (fromDate && fineDate < DateTime.fromISO(fromDate)) return false;
            if (toDate && fineDate > DateTime.fromISO(toDate)) return false;
            return true;
          });
        });
      }

      this.setState({ outstandingFines: filteredFines, loading: false });

      // Notify parent component of data change
      if (this.props.onDataChange) {
        this.props.onDataChange(filteredFines);
      }
    } catch (error) {
      this.setState({
        error: `Failed to fetch outstanding fines: ${error.message}`,
        outstandingFines: [],
        loading: false,
      });
    }
  };

  processOutstandingFines = (fines) => {
    const patronFinesMap = new Map();

    fines.forEach((fine, index) => {
      const patronKey = fine.email || fine.name || "Unknown Patron";

      if (!patronFinesMap.has(patronKey)) {
        patronFinesMap.set(patronKey, {
          patronName: fine.name || "Unknown Patron",
          patronEmail: fine.email || "No email",
          totalFineAmount: 0,
          fineBreakdown: [],
          paymentStatus: "Unpaid",
          paidAmount: 0,
          patronId: fine.profile_id || patronKey,
        });
      }

      const patronRecord = patronFinesMap.get(patronKey);
      const fineAmount = parseFloat(fine.total_fine || 0);

      patronRecord.totalFineAmount += fineAmount;
      patronRecord.fineBreakdown.push({
        fineId: fine.id,
        itemTitle: fine.item_title || "Unknown Item",
        loanId: fine.loan_id,
        amount: fineAmount,
        status: fine.status || "unpaid",
        date: fine.start_date || fine.creation_date || fine.created_at,
        itemBarcode: fine.item_barcode,
        dueDate: fine.due_date,
      });

      if (fine.status === "paid") {
        patronRecord.paidAmount += fineAmount;
      }
    });

    const result = Array.from(patronFinesMap.values()).map((patron) => ({
      ...patron,
      paymentStatus: this.calculatePaymentStatus(
        patron.paidAmount,
        patron.totalFineAmount
      ),
    }));

    return result;
  };

  calculatePaymentStatus = (paidAmount, totalAmount) => {
    if (totalAmount === 0) return "No Fine";
    if (paidAmount >= totalAmount) return "Paid";
    return "Unpaid";
  };

  formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(amount);
  };

  formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_MED);
  };

  renderOutstandingFinesTable() {
    const { outstandingFines } = this.state;

    if (!outstandingFines || outstandingFines.length === 0) {
      return (
        <Message info>
          <Message.Header>No Outstanding Fines Found</Message.Header>
          <p>There are currently no outstanding fines in the system.</p>
        </Message>
      );
    }

    return (
      <Table
        celled
        striped
        style={{
          fontSize: "14px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Table.Header>
          <Table.Row style={{ backgroundColor: "#f8f9fa" }}>
            <Table.HeaderCell
              style={{
                fontWeight: "600",
                color: "#2c3e50",
                padding: "16px",
                borderBottom: "2px solid #dee2e6",
                textAlign: "center",
              }}
            >
              Patron Information
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{
                fontWeight: "600",
                color: "#2c3e50",
                padding: "16px",
                borderBottom: "2px solid #dee2e6",
                textAlign: "center",
              }}
            >
              Fine Breakdown
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{
                fontWeight: "600",
                color: "#2c3e50",
                padding: "16px",
                borderBottom: "2px solid #dee2e6",
                textAlign: "center",
              }}
            >
              Payment Status
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{
                fontWeight: "600",
                color: "#2c3e50",
                padding: "16px",
                borderBottom: "2px solid #dee2e6",
                textAlign: "center",
              }}
            >
              Total Amount
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {outstandingFines.map((patron, index) => (
            <React.Fragment key={index}>
              <Table.Row
                style={{
                  backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                }}
              >
                <Table.Cell
                  style={{
                    padding: "20px",
                    verticalAlign: "top",
                    borderRight: "1px solid #dee2e6",
                  }}
                >
                  <div style={{ marginBottom: "8px" }}>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#2c3e50",
                        marginBottom: "8px",
                      }}
                    >
                      {patron.patronName}
                    </div>
                    {patron.patronEmail &&
                      patron.patronEmail !== "No email" && (
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#6c757d",
                            marginTop: "4px",
                          }}
                        >
                          {patron.patronEmail}
                        </div>
                      )}
                  </div>
                </Table.Cell>

                <Table.Cell
                  style={{
                    padding: "20px",
                    verticalAlign: "top",
                    borderRight: "1px solid #dee2e6",
                  }}
                >
                  {patron.fineBreakdown.map((fine, fineIndex) => (
                    <div
                      key={fineIndex}
                      style={{
                        marginBottom:
                          fineIndex < patron.fineBreakdown.length - 1
                            ? "16px"
                            : "0",
                        padding: "12px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px",
                        borderLeft: "4px solid #dc3545",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#2c3e50",
                          marginBottom: "6px",
                          lineHeight: "1.4",
                        }}
                      >
                        {fine.itemTitle}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#6c757d",
                            backgroundColor: "#ffffff",
                            padding: "2px 6px",
                            borderRadius: "3px",
                            border: "1px solid #dee2e6",
                          }}
                        >
                          Amount: {this.formatCurrency(fine.amount)}
                        </span>
                        {fine.itemBarcode && (
                          <span
                            style={{
                              fontSize: "11px",
                              color: "#6c757d",
                              backgroundColor: "#ffffff",
                              padding: "2px 6px",
                              borderRadius: "3px",
                              border: "1px solid #dee2e6",
                            }}
                          >
                            Accession No: {fine.itemBarcode}
                          </span>
                        )}
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#6c757d",
                            backgroundColor: "#ffffff",
                            padding: "2px 6px",
                            borderRadius: "3px",
                            border: "1px solid #dee2e6",
                          }}
                        >
                          Due Date: {this.formatDate(fine.date)}
                        </span>
                      </div>
                    </div>
                  ))}
                </Table.Cell>

                <Table.Cell
                  style={{
                    padding: "20px",
                    verticalAlign: "middle",
                    textAlign: "center",
                    borderRight: "1px solid #dee2e6",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color:
                        patron.paymentStatus === "Paid"
                          ? "#28a745"
                          : patron.paymentStatus === "Partially Paid"
                          ? "#ffc107"
                          : patron.paymentStatus === "No Fine"
                          ? "#007bff"
                          : "#dc3545",
                      textAlign: "center",
                      marginBottom: "8px",
                    }}
                  >
                    {patron.paymentStatus}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6c757d",
                      backgroundColor: "#f8f9fa",
                      padding: "6px 10px",
                      borderRadius: "4px",
                      textAlign: "center",
                    }}
                  >
                    {patron.fineBreakdown.length} fine(s)
                  </div>
                  {patron.paidAmount > 0 && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6c757d",
                        marginTop: "4px",
                        textAlign: "center",
                      }}
                    >
                      Paid: {this.formatCurrency(patron.paidAmount)}
                    </div>
                  )}
                </Table.Cell>

                <Table.Cell
                  style={{
                    padding: "20px",
                    verticalAlign: "middle",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Label
                      size="large"
                      color={patron.totalFineAmount > 0 ? "red" : "green"}
                      style={{
                        fontSize: "18px",
                        fontWeight: "700",
                        padding: "12px 16px",
                      }}
                    >
                      {this.formatCurrency(patron.totalFineAmount)}
                    </Label>
                  </div>
                </Table.Cell>
              </Table.Row>

              {index < outstandingFines.length - 1 && (
                <Table.Row>
                  <Table.Cell
                    colSpan="4"
                    style={{
                      padding: "0",
                      height: "12px",
                      backgroundColor: "#e9ecef",
                      borderTop: "2px solid #dee2e6",
                      borderBottom: "2px solid #dee2e6",
                    }}
                  />
                </Table.Row>
              )}
            </React.Fragment>
          ))}
        </Table.Body>
      </Table>
    );
  }

  render() {
    const { loading, error } = this.state;

    if (error) {
      return (
        <Segment>
          <Message negative>
            <Message.Header>Something went wrong</Message.Header>
            <p>{error}</p>
          </Message>
        </Segment>
      );
    }

    return (
      <Segment loading={loading}>
        <Header as="h3" dividing>
          <Icon name="money bill alternate" color="green" />
          <Header.Content>
            Outstanding Fines Report
            <Header.Subheader style={{ fontSize: "0.7em", color: "#666" }}>
              Detailed breakdown of all outstanding fines by patron with payment
              status.
            </Header.Subheader>
          </Header.Content>
        </Header>
        {this.renderOutstandingFinesTable()}
      </Segment>
    );
  }
}

OutstandingFinesTab.propTypes = {
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
  onDataChange: PropTypes.func,
};

OutstandingFinesTab.defaultProps = {
  fromDate: "",
  toDate: "",
  onDataChange: null,
};

export default OutstandingFinesTab;
